import { AnalyticsID } from 'lib/analytics/constants';
import { env } from 'lib/constants/env';
import { COOKIEBOT_ENABLED, GEOREGIONS } from 'lib/cookiebot';
import { type ScriptCondition } from 'lib/scriptManager';
import { ScriptID } from 'lib/scriptManager/constants';
import { useCallback, useEffect, useState } from 'react';
import ReactGA from 'react-ga4';
import { useDeepCompareEffect } from 'react-use';
import { isProxiedUrl } from 'utils/isProxiedUrl';

import { useScriptManagerStore } from './useScriptManagerStore';

export type ScriptConditions = Partial<Record<ScriptCondition, boolean>>;

export const useScriptManager = (conditions?: ScriptConditions) => {
  const scriptManager = useScriptManagerStore((state) => state.scriptManager);
  const [, forceUpdate] = useState({});

  useEffect(() => {
    if (COOKIEBOT_ENABLED) {
      scriptManager.registerScript({
        id: AnalyticsID.CookieBot,
        strategy: 'afterInteractive',
        src: `https://consent.cookiebot.com/uc.js`,
        attributes: {
          'data-cbid': env.NEXT_PUBLIC_COOKIEBOT_DEFAULT_CBID,
          'data-blockingmode': 'auto',
          'data-georegions': GEOREGIONS,
          type: 'text/javascript',
        },
        priority: true,
      });
    }

    if (env.NEXT_PUBLIC_GA_ID) {
      scriptManager.registerScript({
        id: AnalyticsID.GTM,
        strategy: 'afterInteractive',
        conditions: {
          OR: ['cookiesAccepted'],
        },
        content: `
        (function(w,d,s,l,i){w[l]=w[l]||[];w[l].push({'gtm.start':
        new Date().getTime(),event:'gtm.js'});var f=d.getElementsByTagName(s)[0],
        j=d.createElement(s),dl=l!='dataLayer'?'&l='+l:'';j.async=true;j.src=
        'https://www.googletagmanager.com/gtm.js?id='+i+dl;f.parentNode.insertBefore(j,f);
        })(window,document,'script','dataLayer', '${env.NEXT_PUBLIC_GA_ID}');
      `,
        loadCallback: () => {
          if (!env.NEXT_PUBLIC_GA_ID) return;
          ReactGA.initialize(env.NEXT_PUBLIC_GA_ID);
        },
      });
    }

    if (env.NEXT_PUBLIC_CONVERGE_PIXEL_URL) {
      const convergePixelUrl = env.NEXT_PUBLIC_CONVERGE_PIXEL_URL;
      const isConvergeProxied = isProxiedUrl(
        convergePixelUrl,
        env.NEXT_PUBLIC_SITE_URL,
        '/cvg/',
      );

      scriptManager.registerScript({
        id: AnalyticsID.ConvergePixel,
        strategy: 'afterInteractive',
        src: convergePixelUrl,
        conditions: {
          OR: ['cookiesAccepted'],
        },
      });

      scriptManager.registerScript({
        id: AnalyticsID.Converge,
        strategy: 'afterInteractive',
        content: `
window.cvg||(cvg=function(){cvg.process?cvg.process.apply(cvg,arguments):cvg.queue.push(arguments)},cvg.queue=[]);
${isConvergeProxied ? `cvg({method: "proxy", tracking: "${new URL('/cvg', env.NEXT_PUBLIC_SITE_URL).toString()}", static: "${new URL('/cvg/static', env.NEXT_PUBLIC_SITE_URL).toString()}"});\n` : ''}
cvg({method:"track",eventName:"$page_load"});
  `,
        conditions: {
          OR: ['cookiesAccepted'],
        },
      });
    }

    if (env.NEXT_PUBLIC_VIBE_GALLERIES_ENABLED) {
      scriptManager.registerScript({
        id: ScriptID.VibeGallery,
        strategy: 'afterInteractive',
        content: `var CRL8_SITENAME = 'timelineskin-xlhjpi';
!function(){var e=window.crl8=window.crl8||{},n=!1,i=[];
e.ready=function(e){n?e():i.push(e)},e.pixel=e.pixel||function(){e.pixel.q.push(arguments)},e.pixel.q=e.pixel.q||[];
var t=window.document,o=t.createElement("script"),c=e.debug||-1!==t.location.search.indexOf("crl8-debug=true")?"js":"min.js";
o.async=!0,o.src=t.location.protocol+"//edge.curalate.com/sites/"+CRL8_SITENAME+"/site/latest/site."+c,o.onload=function(){n=!0,i.forEach(function(e){e()})};
var r=t.getElementsByTagName("script")[0];r.parentNode.insertBefore(o,r.nextSibling)}();`,
        conditions: {
          OR: ['isVibeGalleryOnPage'],
        },
        loadCallback: () => {
          window.dispatchEvent(new Event('crl8-loaded'));
        },
      });
    }

    scriptManager.registerScript({
      id: ScriptID.Trustpilot,
      strategy: 'afterInteractive',
      src: 'https://widget.trustpilot.com/bootstrap/v5/tp.widget.bootstrap.min.js',
    });

    forceUpdate({});

    return () => {
      scriptManager.clearScripts();
    };
  }, [scriptManager]);

  /* INFO: Subscribe to condition changes and update hook state */
  useEffect(() => {
    const unsubscribe = scriptManager.subscribe(() => {
      forceUpdate({});
    });
    return unsubscribe;
  }, [scriptManager]);

  useDeepCompareEffect(() => {
    if (!conditions) return;
    Object.entries(conditions).forEach(([key, value]) => {
      scriptManager.setCondition(key as ScriptCondition, value);
    });
  }, [scriptManager, conditions]);

  const elements = scriptManager.getScriptElements();

  const setCondition = useCallback(
    (key: ScriptCondition, value: boolean) => {
      scriptManager.setCondition(key, value);
    },
    [scriptManager],
  );

  return { scriptManager, elements, setCondition };
};

export const useScriptCondition = () => {
  const scriptManager = useScriptManagerStore((state) => state.scriptManager);

  const setCondition = useCallback(
    (key: ScriptCondition, value: boolean) => {
      scriptManager.setCondition(key, value);
    },
    [scriptManager],
  );

  return setCondition;
};
